import { z } from 'zod';
/**
 * What a commentary run actually did — the debugging/refinement record
 * that rides into the "SKJV It is Written Report" metadata block.
 *
 * Everything here is server-observed fact about ONE generation: how long
 * it took, which LLMs were called and how often, what the guards and the
 * content validator said. A bad answer is usually explained by one of
 * these numbers (a re-roll, a missed cross-ref floor, a cold model load),
 * so the report carries them beside the output.
 */
/** One LLM the run actually called. Several tasks share a model. */
export const RunModelSchema = z.object({
    /** ModelTask key — 'bible-chat', 'guard-judge'. */
    task: z.string(),
    /** Ollama model tag as configured, e.g. 'qwen3.6:35b-a3b'. */
    model: z.string(),
    /** Plain-language job, for a reader who does not know the task keys. */
    role: z.string(),
    /** Invocations, re-rolls and judge retries included. 0 = never needed. */
    calls: z.number(),
});
export const RunValidationSchema = z.object({
    unlistedCrossRefs: z.array(z.string()),
    missingCrossRefFloor: z.boolean(),
    fabricatedQuotes: z.array(z.string()),
    unlistedStrongs: z.array(z.string()),
    badStrongs: z.array(z.string()),
    stockClosers: z.array(z.string()),
    /** Count only — the paragraphs themselves are already in the body. */
    duplicateParagraphs: z.number(),
});
export const RunVerseAnchorsSchema = z.object({
    totalBlocks: z.number(),
    anchoredBlocks: z.number(),
    coverageRatio: z.number(),
    outOfRange: z.array(z.string()),
    nonAscending: z.boolean(),
});
export const CommentaryRunMetaSchema = z.object({
    generatedAt: z.string(),
    /** Wall time inside the route: first DB lookup → finished body. */
    durationMs: z.number(),
    mode: z.enum(['verse', 'pericope']),
    /** The canonical range the model actually expounded. */
    passageReference: z.string(),
    verseCount: z.number(),
    models: z.array(RunModelSchema),
    /** Ollama metrics for the LAST generation call (the body that shipped). */
    inference: z.object({
        promptTokens: z.number(),
        completionTokens: z.number(),
        tokensPerSecond: z.number(),
        loadDurationMs: z.number(),
    }).optional(),
    rolls: z.object({
        /** Generation calls, including every re-roll. 1 = clean first pass. */
        generations: z.number(),
        anchorRerolls: z.number(),
        guardReasks: z.number(),
    }),
    guard: z.object({
        outcome: z.enum(['clean', 'regenerated', 'refused']),
        violations: z.array(z.string()),
    }),
    validation: RunValidationSchema.optional(),
    verseAnchors: RunVerseAnchorsSchema.optional(),
    /** Cross-refs the prompt offered the model (the hallucination ground truth). */
    allowListSize: z.number(),
    promptChars: z.number(),
    /** Reader "Any thoughts?" seed length the MODEL saw — 0 when none rode in
     *  (verse mode gates them out even when the reader typed some). */
    readerThoughtsChars: z.number(),
    engine: z.object({
        hostname: z.string(),
        ollamaHost: z.string(),
    }),
});
//# sourceMappingURL=commentary-run-meta.js.map